/**
 * Tutorial Card Canvas Block Component
 *
 * sl-728o.7.1: Renders tutorial cards on the edgeless canvas.
 *
 * Users can:
 * - See tutorial title + description
 * - Click "Start" to begin the tutorial (with RBAC + prerequisite checks)
 * - See disabled state for coming-soon tutorials
 */

import { Bound } from '@blocksuite/global/gfx';
import { BlockComponent, toGfxBlockComponent } from '@blocksuite/std';
import { GfxViewInteractionExtension } from '@blocksuite/std/gfx';
import { html, nothing } from 'lit';
import { state } from 'lit/decorators.js';

import type { TutorialCardBlockModel } from '../schemas/tutorial-card-model.js';
import { TutorialCardBlockSchema } from '../schemas/tutorial-card-model.js';
import * as styles from './tutorial-card-canvas-block.css.js';

export const AFFINE_TUTORIAL_CARD_CANVAS = 'affine-tutorial-card-canvas';

// =============================================================================
// Base Block Component (for doc view — renders nothing, canvas-only)
// =============================================================================

export class TutorialCardBlockComponent extends BlockComponent<TutorialCardBlockModel> {
  override renderBlock() {
    return nothing;
  }
}

// =============================================================================
// Canvas Block Component
// =============================================================================

export class TutorialCardCanvasBlockComponent extends toGfxBlockComponent(
  TutorialCardBlockComponent
) {
  @state()
  private accessor _editing = false;

  override connectedCallback(): void {
    super.connectedCallback();

    const selection = this.gfx.selection;
    this._editing = selection.has(this.model.id) && selection.editing;
    this._disposables.add(
      selection.slots.updated.subscribe(() => {
        this._editing = selection.has(this.model.id) && selection.editing;
      })
    );
  }

  override renderGfxBlock() {
    const { model } = this;
    const title = model.displayTitle;
    const description = model.displayDescription;
    const disabled = model.isDisabled;
    const disabledReason = model.props.disabledReason;

    return html`
      <div class=${styles.cardContainer} data-editing=${this._editing}>
        <div class=${styles.content}>
          <div class=${styles.title}>${title}</div>
          ${description
            ? html`<div class=${styles.description}>${description}</div>`
            : nothing}
          <button
            class="${styles.startButton} ${disabled
              ? styles.startButtonDisabled
              : ''}"
            ?disabled=${disabled}
            @click=${this._handleStart}
          >
            ${this._renderPlayIcon()}
            <span>${disabled ? 'Not available' : 'Start'}</span>
          </button>
          ${disabled && disabledReason
            ? html`<div class=${styles.disabledReason}>${disabledReason}</div>`
            : nothing}
        </div>
      </div>
    `;
  }

  // ===========================================================================
  // Icons
  // ===========================================================================

  private _renderPlayIcon() {
    return html`
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="currentColor"
        stroke="none"
      >
        <polygon points="5 3 19 12 5 21 5 3" />
      </svg>
    `;
  }

  // ===========================================================================
  // Event Handlers
  // ===========================================================================

  private readonly _handleStart = (e: Event) => {
    e.stopPropagation();
    e.preventDefault();

    if (this.model.isDisabled) return;

    const sessionId = this.model.props.tutorialSessionId;
    const requiredRole = this.model.props.requiredRole;

    // Dispatch event for React layer to handle RBAC + session start
    window.dispatchEvent(
      new CustomEvent('tutorial-card-start', {
        detail: {
          sessionId,
          requiredRole,
          blockId: this.model.id,
        },
      })
    );
  };
}

declare global {
  interface HTMLElementTagNameMap {
    [AFFINE_TUTORIAL_CARD_CANVAS]: TutorialCardCanvasBlockComponent;
  }
}

// =============================================================================
// Interaction Extension
// =============================================================================

export const TutorialCardBlockInteraction =
  GfxViewInteractionExtension<TutorialCardCanvasBlockComponent>(
    TutorialCardBlockSchema.model.flavour,
    {
      handleRotate: () => ({
        beforeRotate(context) {
          context.set({ rotatable: false });
        },
      }),

      handleResize: ({ model }) => ({
        onResizeStart(context) {
          context.default(context);
        },
        onResizeMove(context) {
          const { newBound, constraint } = context;
          const { minWidth = 200, maxWidth = 400 } = constraint;

          newBound.w = Math.max(minWidth, Math.min(newBound.w, maxWidth));

          // Keep original height
          const originalBound = Bound.deserialize(model.xywh);
          newBound.h = originalBound.h;

          model.xywh = newBound.serialize();
        },
        onResizeEnd(context) {
          context.default(context);
        },
      }),

      handleSelection: ({ gfx, model }) => ({
        onSelect(context) {
          const { selected, multiSelect } = context;
          const alreadySelected = gfx.selection.has(model.id);

          if (!multiSelect && selected && alreadySelected) {
            gfx.selection.set({
              elements: [model.id],
              editing: true,
            });
          } else {
            context.default(context);
          }
        },
      }),
    }
  );
